import { type FC, useMemo } from 'react';

import HandymanOutlined from '@mui/icons-material/HandymanOutlined';
import HomeOutlined from '@mui/icons-material/HomeOutlined';
import SummarizeOutlined from '@mui/icons-material/SummarizeOutlined';
import SupportOutlined from '@mui/icons-material/SupportOutlined';

import { Stack, type StackProps } from '@craft/ui/components/structural';
import { mergeSx } from '@craft/ui/util';

import { SideNavButton } from '@/src/components/atoms/navButtons';
import { Environment } from '@/src/environment';
import { HasAccess } from '@/src/features/auth/components/roles/HasAccess';
import { useSelectedRole } from '@/src/features/auth/hooks';
import { IfEnvironment } from '@/src/features/config/components/IfEnvironment';

import { getRoleNavLinks } from './helpers';

export interface CraftSideNavProps extends Omit<StackProps, 'children' | 'direction'> {}
/**
 * Sidebar navigation for the application. Configures itself based on the selected role. Admins
 * are further configured by the organization type.
 *
 * @see {@link https://www.figma.com/file/kZ6spnfsAZYyn90Ovub3LV/Craft-Component-Library?type=design&node-id=46%3A489&mode=design&t=IRAgh7NBECuMyob1-1|Design}
 */
export const CraftSideNav: FC<CraftSideNavProps> = props => {
  const { organizationType, role } = useSelectedRole();

  const RoleNavLinks = useMemo(
    () => getRoleNavLinks(role, organizationType),
    [role, organizationType],
  );

  return (
    <Stack gap={1} padding={0.5} {...props} sx={mergeSx(props.sx, { overflowX: 'hidden' })}>
      <SideNavButton icon={HomeOutlined} page='home'>
        Home
      </SideNavButton>
      <HasAccess.IsNotFacilitator>
        <SideNavButton icon={SummarizeOutlined} page='reports'>
          Reports
        </SideNavButton>
      </HasAccess.IsNotFacilitator>
      {RoleNavLinks && <RoleNavLinks />}
      <IfEnvironment environment={Environment.Demo}>
        <SideNavButton
          href='https://crafteligibilitypoc.web.app/'
          icon={HandymanOutlined}
          isExternal
        >
          Rap Wizard
        </SideNavButton>
      </IfEnvironment>
      <SideNavButton icon={SupportOutlined} page='support'>
        Support
      </SideNavButton>
    </Stack>
  );
};
